import { GrowthChart } from "@/components/admin/GrowthChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Building2,
	ClipboardList,
	FileSpreadsheet,
	HardDrive,
	Package,
	ShoppingCart,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/")({
	component: AdminDashboard,
});

const adminClient = createClient(AdminService, transport);

function formatBytes(bytes: bigint): string {
	const num = Number(bytes);
	if (num < 1024) return `${num} B`;
	if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
	if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
	return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function AdminDashboard() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([{ label: t("admin_root") }]);

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "metrics"],
		queryFn: () => adminClient.getUsageMetrics({ days: 30 }),
	});

	const metrics = data?.metrics;

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
				<MetricCard
					title={t("dashboard_total_workspaces")}
					icon={Building2}
					value={metrics?.totalWorkspaces}
					subtitle={t("dashboard_active_count", {
						count: Number(metrics?.activeWorkspaces ?? 0),
					})}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_total_users")}
					icon={Users}
					value={metrics?.totalUsers}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_total_products")}
					icon={Package}
					value={metrics?.totalProducts}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_total_orders")}
					icon={ShoppingCart}
					value={metrics?.totalOrders}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_total_po_rows")}
					icon={ClipboardList}
					value={metrics?.totalPoRows}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_catalog_import")}
					icon={FileSpreadsheet}
					value={metrics?.totalCatalogRows}
					isLoading={isLoading}
				/>
				<MetricCard
					title={t("dashboard_storage")}
					icon={HardDrive}
					value={metrics ? formatBytes(metrics.totalStorageBytes) : undefined}
					isLoading={isLoading}
					isText
				/>
			</div>

			<div className="grid gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="text-base">
							{t("dashboard_workspace_growth")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<Skeleton className="h-32 w-full" />
						) : (
							<GrowthChart
								points={metrics?.workspaceGrowth ?? []}
								color="hsl(217 91% 60%)"
							/>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-base">
							{t("dashboard_user_growth")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<Skeleton className="h-32 w-full" />
						) : (
							<GrowthChart
								points={metrics?.userGrowth ?? []}
								color="hsl(142 71% 45%)"
							/>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

interface MetricCardProps {
	title: string;
	icon: React.ElementType;
	value?: bigint | number | string;
	subtitle?: string;
	isLoading?: boolean;
	isText?: boolean;
}

function MetricCard({
	title,
	icon: Icon,
	value,
	subtitle,
	isLoading,
	isText,
}: MetricCardProps) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium">{title}</CardTitle>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<Skeleton className="h-8 w-24" />
				) : (
					<>
						<div className="text-2xl font-bold">
							{isText ? value : (value?.toLocaleString() ?? "0")}
						</div>
						{subtitle && (
							<p className="text-xs text-muted-foreground">{subtitle}</p>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}
